/* ── Activity Tracking — localStorage helpers ──────────────────────────
   All keys are user-scoped (email suffix) to prevent cross-user bleed.

   ts_streak_{email}   — { days, last }
   ts_daily_{email}    — { date, done }      ← tasks completed today
   ts_weekly_{email}   — { "Tue May 20": 3 } ← last-30-days task activity
   ts_vcomp_{email}    — { date, count }     ← videos watched to end today
──────────────────────────────────────────────────────────────────────── */

/* ── Key helpers ───────────────────────────────────────────── */
export const streakKey  = (email) => `ts_streak_${email}`;
export const dailyKey   = (email) => `ts_daily_${email}`;
export const weeklyKey  = (email) => `ts_weekly_${email}`;
export const videoCompKey = (email) => `ts_vcomp_${email}`;

/* ── Streak ─────────────────────────────────────────────────── */
export function getOrUpdateStreak(email) {
  try {
    const today     = new Date().toDateString();
    const yesterday = new Date(Date.now() - 86400000).toDateString();
    const raw       = localStorage.getItem(streakKey(email));
    const data      = raw ? JSON.parse(raw) : { days: 0, last: '' };
    if (data.last === today)     return data.days;
    if (data.last === yesterday) {
      const next = { days: data.days + 1, last: today };
      localStorage.setItem(streakKey(email), JSON.stringify(next));
      return next.days;
    }
    const reset = { days: 1, last: today };
    localStorage.setItem(streakKey(email), JSON.stringify(reset));
    return 1;
  } catch { return 1; }
}

/* ── Daily goal progress ────────────────────────────────────── */
export function getDailyProgress(email) {
  try {
    const today = new Date().toDateString();
    const raw   = localStorage.getItem(dailyKey(email));
    const data  = raw ? JSON.parse(raw) : { date: '', done: 0 };
    return data.date === today ? data.done : 0;
  } catch { return 0; }
}

export function incrementDailyProgress(email) {
  try {
    const today = new Date().toDateString();
    const raw   = localStorage.getItem(dailyKey(email));
    const data  = raw ? JSON.parse(raw) : { date: '', done: 0 };
    const done  = data.date === today ? data.done + 1 : 1;
    localStorage.setItem(dailyKey(email), JSON.stringify({ date: today, done }));
    return done;
  } catch { return 1; }
}

/* ── Weekly activity graph ──────────────────────────────────── */
export function recordDailyActivity(email, delta = 1) {
  try {
    const today = new Date().toDateString();
    const raw   = localStorage.getItem(weeklyKey(email));
    const data  = raw ? JSON.parse(raw) : {};
    data[today] = (data[today] || 0) + delta;

    /* Prune entries older than 30 days */
    const cutoff = Date.now() - 30 * 86400000;
    for (const key of Object.keys(data)) {
      if (new Date(key).getTime() < cutoff) delete data[key];
    }
    localStorage.setItem(weeklyKey(email), JSON.stringify(data));
  } catch { /* ignore */ }
}

export function getWeeklyActivity(email) {
  try {
    const raw = localStorage.getItem(weeklyKey(email));
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

/* ── Daily video completions ────────────────────────────────── */
export function recordVideoComplete(email) {
  if (!email) return;
  try {
    const today = new Date().toDateString();
    const raw   = localStorage.getItem(videoCompKey(email));
    const data  = raw ? JSON.parse(raw) : { date: '', count: 0 };
    const count = data.date === today ? data.count + 1 : 1;
    localStorage.setItem(videoCompKey(email), JSON.stringify({ date: today, count }));
  } catch { /* storage full — ignore */ }
}

export function getDailyVideoCount(email) {
  if (!email) return 0;
  try {
    const today = new Date().toDateString();
    const raw   = localStorage.getItem(videoCompKey(email));
    if (!raw) return 0;
    const { date, count } = JSON.parse(raw);
    return date === today ? count : 0;
  } catch { return 0; }
}
